import { useTranslation } from 'react-i18next';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import { ChevronDown } from 'lucide-react';
import { usePreferencesStore, type Currency } from '../../stores/preferences.js';
import { pushToast } from '../app/toast-store.js';

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'GBP', label: 'GBP (£)' },
] as const;

export function CurrencySection() {
  const { t } = useTranslation('settings');
  const currency = usePreferencesStore((s) => s.currency);
  const setCurrency = usePreferencesStore((s) => s.setCurrency);

  const currentLabel =
    CURRENCY_OPTIONS.find((o) => o.value === currency)?.label ?? CURRENCY_OPTIONS[0].label;

  function handleChange(newCurrency: Currency) {
    setCurrency(newCurrency);
    pushToast({ tone: 'success', title: t('currency.saved') });
  }

  return (
    <section aria-labelledby="settings-currency-heading" className="mb-2">
      <h2 id="settings-currency-heading" className="text-h2 font-semibold text-primary mb-4">
        {t('currency.heading')}
      </h2>
      <Listbox value={currency} onChange={handleChange}>
        <p className="block text-small text-secondary uppercase tracking-wider mb-1">
          {t('currency.label')}
        </p>
        <ListboxButton
          data-testid="currency-listbox"
          data-currency={currency}
          className="w-full bg-transparent border-0 border-b border-white/[.04] focus:border-white focus:outline-none px-0 py-2 text-primary text-left flex items-center justify-between"
        >
          <span>{currentLabel}</span>
          <ChevronDown className="h-4 w-4 text-secondary" aria-hidden="true" />
        </ListboxButton>
        <ListboxOptions className="mt-2 bg-white/[.03] backdrop-blur-sm border border-white/[.04] rounded-lg overflow-hidden focus:outline-none z-10 transition data-[closed]:opacity-0 data-[open]:opacity-100 motion-safe:duration-100">
          {CURRENCY_OPTIONS.map((opt) => (
            <ListboxOption
              key={opt.value}
              value={opt.value}
              className="group px-4 py-2 text-body text-primary cursor-pointer data-[focus]:bg-white/[.05] data-[selected]:text-accent-lime"
            >
              {opt.label}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Listbox>
    </section>
  );
}
